import React, { useEffect, useState } from "react";
import { useMutationState } from "@tanstack/react-query";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  IconButton,
  Divider,
  Avatar,
  Chip,
  Fade,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  AccountBalance as IncomeIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import Category from "../../models/finance/Category";
import Income from "../../models/finance/Income";
import { ModernTextField, GradientButton } from "../common/StyledComponents";
import AmountInput from "../common/AmountInput";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useNavigate } from "react-router-dom";
import { CATEGORIES_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import { getCurrencySymbol } from "../../utils/currencies";
import { useUserProfileContext } from "../../contexts/ProfileContext";

interface IncomeFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    id: string,
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => void;
  onSaveAndAdd: (
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => void,
  onDelete?: (income: Income) => void;
  mode: "add" | "edit";
  initialData?: Income;
  selectedYear: number;
  selectedMonth: number;
  categories: Category[];
}

const IncomeFormDrawer: React.FC<IncomeFormDrawerProps> = ({
  open,
  onClose,
  onSave,
  onSaveAndAdd,
  onDelete,
  mode,
  initialData,
  selectedYear,
  selectedMonth,
  categories,
}) => {
  const theme = useTheme();
  const { dateFormat } = useUserProfileContext();
  const navigate = useNavigate();
  const { currency } = useCurrencyFormatter();
  const currencySymbol = getCurrencySymbol(currency);
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const { error, helpText, clearError } = useError();
  
  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const setFieldError = (field: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };
  const clearAllFieldErrors = () => setFieldErrors({});

  // ✅ Observe income mutation state globally (no prop drilling!)
  const isSaving = useMutationState({
    filters: { mutationKey: ['incomes'], status: 'pending' },
    select: (mutation) => mutation.state.status === 'pending',
  }).some(Boolean);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setDate(dayjs(initialData.date));
      setAmount(initialData.amount.toString());
      setDescription(initialData.description);
      setCategory(initialData.category);
    } else {
      // Default to today's date when adding a new income
      setDate(dayjs());
      setAmount("");
      setDescription("");
      setCategory("");
    }
    clearError();
    clearAllFieldErrors();
  }, [mode, initialData, selectedYear, selectedMonth, open, clearError]);

  const handleSave = () => {
    // Clear previous errors
    clearError();
    clearAllFieldErrors();
    let hasErrors = fieldsValidation();
    
    if (hasErrors) {
      return;
    }

    const numAmount = parseFloat(amount);
    const incomeId = initialData?.id || "";
    onSave(incomeId, date!.toDate(), numAmount, description, category);
  };

  const handleSaveAndNew = () => {
    // Clear previous errors
    clearError();
    clearAllFieldErrors();
    let hasErrors = fieldsValidation();
    
    if (hasErrors) {
      return;
    }

    const numAmount = parseFloat(amount);
    onSaveAndAdd(date!.toDate(), numAmount, description, category);
    setAmount("");
    setDescription("");
  };

  const fieldsValidation = () => {
        // Field-level validation
    let hasErrors = false;
    
    if (!date) {
      setFieldError("date", "Date is required");
      hasErrors = true;
    }
    
    if (!amount || amount.trim() === "") {
      setFieldError("amount", "Amount is required");
      hasErrors = true;
    } else {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        setFieldError("amount", "Amount must be greater than 0");
        hasErrors = true;
      }
    }
    
    if (!description || description.trim() === "") {
      setFieldError("description", "Description is required");
      hasErrors = true;
    }
    
    if (!category) {
      setFieldError("category", "Category is required");
      hasErrors = true;
    }

    return hasErrors;
  }

  const handleDelete = () => {
    if (initialData && onDelete) {
      onDelete(initialData);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 3, // Higher than header
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          background: theme.palette.background.default,
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              color: "white",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                width: "100px",
                height: "100px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                transform: "translate(30px, -30px)",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <IncomeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {mode === "add" ? "Add Income" : "Edit Income"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {mode === "add" ? "Record a new income" : "Update income details"}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={onClose}
                  sx={{
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              
              {mode === "edit" && (
                <Chip
                  label="Editing Mode"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Form Content */}
          <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
            <ErrorAlert
              error={error}
              helpText={helpText}
              onClear={clearError}
              onRetry={handleSave}
              title="Income Form Error"
              showRetry={false}
            />

            <Stack spacing={3}>
              {/* Date Picker */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={date}
                    format={dateFormat}
                    onChange={(newDate) => {
                      setDate(newDate);
                      clearFieldError("date");
                    }}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { borderRadius: 2 },
                        error: !!fieldErrors.date,
                        helperText: fieldErrors.date,
                        InputProps: {
                          startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              {/* Amount */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Amount
                </Typography>
                <AmountInput
                  fullWidth
                  value={amount}
                  onChange={(value) => {
                    setAmount(value);
                    clearFieldError("amount");
                  }}
                  placeholder="0.00"
                  maxDecimals={2}
                  error={!!fieldErrors.amount}
                  helperText={fieldErrors.amount}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 1, color: 'action.active', fontWeight: 600 }}>
                        {currencySymbol}
                      </Typography>
                    ),
                  }}
                />
              </Box>

              {/* Description */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Description
                </Typography>
                <ModernTextField
                  fullWidth
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearFieldError("description");
                  }}
                  placeholder="What was this income for?"
                  error={!!fieldErrors.description}
                  helperText={fieldErrors.description || `${description.length}/50 characters`}
                  inputProps={{ maxLength: 50 }}
                  InputProps={{
                    startAdornment: <DescriptionIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Box>

              {/* Category */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Category
                </Typography>
                <FormControl fullWidth error={!!fieldErrors.category}>
                  <Select
                    value={category}
                    onChange={(e) => {
                      const value = e.target.value as string;
                      if (value === "__go_to_categories__") {
                        navigate(CATEGORIES_ROUTE_PATH);
                        return;
                      }
                      setCategory(value);
                      clearFieldError("category");
                    }}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      },
                    }}
                    startAdornment={<CategoryIcon sx={{ mr: 1, color: 'action.active' }} />}
                  >
                    <MenuItem value="" disabled>
                      <em>Select a category</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.title}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.success.main,
                            }}
                          />
                          {cat.title}
                        </Box>
                      </MenuItem>
                    ))}
                    <MenuItem value="__go_to_categories__">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon sx={{ color: 'action.active' }} />
                        Add New Category
                      </Box>
                    </MenuItem>
                  </Select>
                  {fieldErrors.category && (
                    <FormHelperText>{fieldErrors.category}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Footer Actions */}
          <Box sx={{ p: 3, backgroundColor: theme.palette.grey[50] }}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              {mode === "edit" && onDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  sx={{
                    borderRadius: 2,
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: theme.palette.error.light + '20',
                      borderColor: theme.palette.error.dark,
                    },
                  }}
                >
                  Delete
                </Button>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={isSaving}
                  sx={{
                    borderRadius: 2,
                    minWidth: 100,
                    borderColor: theme.palette.grey[300],
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.grey[400],
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                >
                  Cancel
                </Button>
                
                <GradientButton
                  startIcon={isSaving ? null : <SaveIcon />}
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    minWidth: 120,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                    },
                  }}
                >
                  {isSaving ? "Saving..." : mode === "add" ? "Save" : "Update"}
                </GradientButton>

                {mode === "add" && (
                  <GradientButton
                    startIcon={isSaving ? null : <SaveIcon />}
                    onClick={handleSaveAndNew}
                    disabled={isSaving}
                    sx={{
                      minWidth: 120,
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      },
                    }}
                  >
                    {isSaving ? "Saving..." : "Save & New"}
                  </GradientButton>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Drawer>
  );
};

export default IncomeFormDrawer;
